import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  adminReports,
  athletes,
  briefs,
  companies,
  contracts,
  conversations,
  defaultMatchWeights,
  deliverables,
  notifications,
  partnerships,
  payments,
  proposals,
  reports,
} from '../data/demo'
import { calculatePaymentBreakdown } from '../lib/finance'
import type {
  AppState,
  Brief,
  ConnectionIntent,
  Contract,
  CurrentUser,
  DeliverableStatus,
  MatchWeights,
  Notification,
  OnboardingState,
  Partnership,
  Proposal,
  Role,
} from '../types/domain'

const STORAGE_KEY = 'athlink-demo-state-v4'

const initialState: AppState = {
  currentUser: null,
  athletes,
  companies,
  briefs,
  conversations,
  proposals,
  contracts,
  partnerships,
  deliverables,
  payments,
  reports,
  notifications,
  connections: [],
  adminReports,
  favorites: ['ath-camille', 'ath-lucas', 'ath-hugo'],
  shortlistFolders: { 'Sélection été 2026': ['ath-camille', 'ath-lucas', 'ath-hugo'] },
  notes: { 'ath-camille': 'Très bon alignement environnement et territoire.', 'ath-lucas': 'À valider : disponibilité pour journée collaborateurs.' },
  matchingWeights: defaultMatchWeights,
  onboarding: null,
  subscription: { athlete: 'premium', company: 'premium' },
}

const demoUsers: Record<Role, CurrentUser> = {
  athlete: { id: 'user-athlete-demo', email: 'athlete.demo@athlink.fr', role: 'athlete', displayName: 'Camille Bernard', athleteId: 'ath-camille', onboardingComplete: true },
  company: { id: 'user-company-demo', email: 'entreprise.demo@athlink.fr', role: 'company', displayName: 'Marc Dubois', companyId: 'co-alpine', onboardingComplete: true },
  admin: { id: 'user-admin-demo', email: 'admin.demo@athlink.fr', role: 'admin', displayName: 'Équipe AthLink', onboardingComplete: true },
}

interface AppStoreValue {
  state: AppState
  loginDemo: (role: Role) => void
  loginWithCredentials: (email: string, password: string) => { ok: boolean; message: string }
  logout: () => void
  startRegistration: (role: 'athlete' | 'company', email: string) => void
  saveOnboarding: (step: number, data: OnboardingState['data']) => void
  completeOnboarding: () => void
  toggleFavorite: (athleteId: string) => void
  addToFolder: (folder: string, athleteId: string) => void
  removeFromFolder: (folder: string, athleteId: string) => void
  setNote: (athleteId: string, note: string) => void
  createBrief: (input: Omit<Brief, 'id' | 'companyId' | 'status' | 'applicants'>) => Brief
  sendMessage: (conversationId: string, body: string) => void
  createConversation: (athleteId: string, subject: string) => string
  inviteAthleteToChat: (athleteId: string, briefId?: string) => string
  applyToBrief: (briefId: string, athleteId: string) => string
  askAboutBrief: (briefId: string, athleteId: string) => string
  createProposal: (input: Partial<Proposal> & Pick<Proposal, 'athleteId' | 'companyId' | 'title' | 'amount'>) => Proposal
  counterProposal: (proposalId: string, amount: number, notes: string) => void
  updateProposalStatus: (proposalId: string, status: Proposal['status']) => void
  generateContract: (proposalId: string) => Contract
  signContract: (contractId: string, role: 'athlete' | 'company') => void
  paySandbox: (contractId: string) => void
  submitProof: (deliverableId: string, caption: string, url?: string) => void
  reviewProof: (deliverableId: string, approved: boolean, comment: string) => void
  updateMatchingWeights: (weights: MatchWeights) => void
  markNotificationRead: (id: string) => void
  markAllNotificationsRead: () => void
  updateAthleteVerification: (athleteId: string, status: AppState['athletes'][number]['verified']) => void
  resolveAdminReport: (reportId: string) => void
  toggleSubscription: (role: 'athlete' | 'company') => void
  updateAthleteProfile: (athleteId: string, patch: Partial<AppState['athletes'][number]>) => void
  updateCompanyProfile: (companyId: string, patch: Partial<AppState['companies'][number]>) => void
  updateAthletePack: (athleteId: string, packId: string, patch: Partial<AppState['athletes'][number]['packs'][number]>) => void
  resetDemo: () => void
}

const StoreContext = createContext<AppStoreValue | null>(null)

function loadInitialState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return structuredClone(initialState)
    const parsed = JSON.parse(raw) as AppState
    return { ...structuredClone(initialState), ...parsed, connections: parsed.connections ?? [], currentUser: parsed.currentUser ?? null }
  } catch {
    return structuredClone(initialState)
  }
}

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(() => loadInitialState())

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  const addNotification = (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
    setState((current) => ({
      ...current,
      notifications: [{ ...notification, id: `not-${crypto.randomUUID()}`, createdAt: new Date().toISOString(), read: false }, ...current.notifications],
    }))
  }

  const value = useMemo<AppStoreValue>(() => ({
    state,
    loginDemo(role) {
      setState((current) => ({ ...current, currentUser: demoUsers[role] }))
    },
    loginWithCredentials(email, password) {
      const role = email.includes('athlete.demo') ? 'athlete' : email.includes('entreprise.demo') ? 'company' : email.includes('admin.demo') ? 'admin' : null
      if (!role || password !== 'AthLink-Demo-2026!') return { ok: false, message: 'Identifiants incorrects. Utilisez un compte de démonstration documenté.' }
      setState((current) => ({ ...current, currentUser: demoUsers[role] }))
      return { ok: true, message: 'Connexion réussie.' }
    },
    logout() {
      setState((current) => ({ ...current, currentUser: null }))
    },
    startRegistration(role, email) {
      const draftUser: CurrentUser = {
        id: `user-${crypto.randomUUID()}`,
        email,
        role,
        displayName: role === 'athlete' ? 'Nouvel athlète' : 'Nouvelle entreprise',
        onboardingComplete: false,
      }
      setState((current) => ({
        ...current,
        currentUser: draftUser,
        onboarding: { role, currentStep: 1, data: { email }, savedAt: new Date().toISOString() },
      }))
    },
    saveOnboarding(step, data) {
      setState((current) => ({
        ...current,
        onboarding: current.onboarding
          ? { ...current.onboarding, currentStep: step, data: { ...current.onboarding.data, ...data }, savedAt: new Date().toISOString() }
          : null,
      }))
    },
    completeOnboarding() {
      setState((current) => ({
        ...current,
        currentUser: current.currentUser ? { ...current.currentUser, onboardingComplete: true, displayName: String(current.onboarding?.data.name || current.onboarding?.data.firstName || current.currentUser.displayName) } : null,
        onboarding: null,
      }))
    },
    toggleFavorite(athleteId) {
      setState((current) => ({
        ...current,
        favorites: current.favorites.includes(athleteId) ? current.favorites.filter((id) => id !== athleteId) : [...current.favorites, athleteId],
      }))
    },
    addToFolder(folder, athleteId) {
      setState((current) => {
        const items = current.shortlistFolders[folder] ?? []
        return { ...current, shortlistFolders: { ...current.shortlistFolders, [folder]: items.includes(athleteId) ? items : [...items, athleteId] } }
      })
    },
    removeFromFolder(folder, athleteId) {
      setState((current) => ({ ...current, shortlistFolders: { ...current.shortlistFolders, [folder]: (current.shortlistFolders[folder] ?? []).filter((id) => id !== athleteId) } }))
    },
    setNote(athleteId, note) {
      setState((current) => ({ ...current, notes: { ...current.notes, [athleteId]: note } }))
    },
    createBrief(input) {
      const created: Brief = { ...input, id: `brief-${crypto.randomUUID()}`, companyId: state.currentUser?.companyId ?? 'co-alpine', status: 'active', applicants: [] }
      setState((current) => ({ ...current, briefs: [created, ...current.briefs] }))
      addNotification({ role: 'company', title: 'Brief publié', body: `Votre brief « ${created.title} » est maintenant visible.`, type: 'brief', href: '/app/entreprise/briefs' })
      return created
    },
    sendMessage(conversationId, body) {
      if (!body.trim()) return
      setState((current) => ({
        ...current,
        conversations: current.conversations.map((conversation) => conversation.id !== conversationId ? conversation : {
          ...conversation,
          messages: [...conversation.messages, {
            id: `m-${crypto.randomUUID()}`,
            senderRole: current.currentUser?.role ?? 'company',
            senderName: current.currentUser?.displayName ?? 'Utilisateur',
            body: body.trim(),
            createdAt: new Date().toISOString(),
            read: true,
          }],
          unreadByAthlete: current.currentUser?.role === 'company' ? conversation.unreadByAthlete + 1 : conversation.unreadByAthlete,
          unreadByCompany: current.currentUser?.role === 'athlete' ? conversation.unreadByCompany + 1 : conversation.unreadByCompany,
        }),
      }))
    },
    createConversation(athleteId, subject) {
      const existing = state.conversations.find((conversation) => conversation.athleteId === athleteId && conversation.companyId === (state.currentUser?.companyId ?? 'co-alpine'))
      if (existing) return existing.id
      const id = `conv-${crypto.randomUUID()}`
      setState((current) => ({
        ...current,
        conversations: [{ id, athleteId, companyId: current.currentUser?.companyId ?? 'co-alpine', subject, contextType: 'profile', unreadByAthlete: 0, unreadByCompany: 0, messages: [] }, ...current.conversations],
      }))
      return id
    },
    inviteAthleteToChat(athleteId, briefId) {
      const companyId = state.currentUser?.companyId ?? 'co-alpine'
      const athlete = state.athletes.find((item) => item.id === athleteId)
      const brief = briefId ? state.briefs.find((item) => item.id === briefId) : undefined
      const existing = state.conversations.find((conversation) => conversation.athleteId === athleteId && conversation.companyId === companyId && (!briefId || conversation.contextId === briefId))
      if (existing) return existing.id
      const conversationId = `conv-${crypto.randomUUID()}`
      const now = new Date().toISOString()
      const connection: ConnectionIntent = { id: `conn-${crypto.randomUUID()}`, athleteId, companyId, briefId, conversationId, initiatedBy: 'company', status: 'conversation_started', createdAt: now, updatedAt: now }
      setState((current) => ({
        ...current,
        conversations: [{
          id: conversationId,
          athleteId,
          companyId,
          subject: brief ? `Échange — ${brief.title}` : `Échange avec ${athlete?.firstName ?? 'un athlète'}`,
          contextType: brief ? 'brief' : 'profile',
          contextId: briefId,
          unreadByAthlete: 1,
          unreadByCompany: 0,
          messages: [{ id: `m-${crypto.randomUUID()}`, senderRole: 'company', senderName: current.currentUser?.displayName ?? 'Entreprise', body: brief ? `Bonjour ${athlete?.firstName ?? ''}, nous souhaitons échanger au sujet de notre opportunité « ${brief.title} ».` : `Bonjour ${athlete?.firstName ?? ''}, votre profil nous intéresse. Seriez-vous disponible pour un premier échange ?`, createdAt: now, read: false }],
        }, ...current.conversations],
        connections: [connection, ...current.connections],
      }))
      addNotification({ role: 'athlete', title: 'Invitation à échanger', body: `${state.currentUser?.displayName ?? 'Une entreprise'} souhaite discuter avec vous.`, type: 'connection', href: `/app/messages?conversation=${conversationId}` })
      return conversationId
    },
    applyToBrief(briefId, athleteId) {
      const brief = state.briefs.find((item) => item.id === briefId)
      if (!brief) throw new Error('Opportunité introuvable.')
      const athlete = state.athletes.find((item) => item.id === athleteId)
      const existing = state.conversations.find((conversation) => conversation.athleteId === athleteId && conversation.companyId === brief.companyId && conversation.contextId === briefId)
      if (existing) return existing.id
      const conversationId = `conv-${crypto.randomUUID()}`
      const now = new Date().toISOString()
      const connection: ConnectionIntent = { id: `conn-${crypto.randomUUID()}`, athleteId, companyId: brief.companyId, briefId, conversationId, initiatedBy: 'athlete', status: 'interest_sent', createdAt: now, updatedAt: now }
      setState((current) => ({
        ...current,
        briefs: current.briefs.map((item) => item.id === briefId && !item.applicants.includes(athleteId) ? { ...item, applicants: [...item.applicants, athleteId] } : item),
        conversations: [{
          id: conversationId,
          athleteId,
          companyId: brief.companyId,
          subject: `Candidature — ${brief.title}`,
          contextType: 'brief',
          contextId: briefId,
          unreadByAthlete: 0,
          unreadByCompany: 1,
          messages: [{ id: `m-${crypto.randomUUID()}`, senderRole: 'athlete', senderName: current.currentUser?.displayName ?? athlete?.firstName ?? 'Athlète', body: `Bonjour, je suis intéressé par l’opportunité « ${brief.title} ». Mon profil correspond aux critères indiqués et je souhaite échanger avec vous.`, createdAt: now, read: false }],
        }, ...current.conversations],
        connections: [connection, ...current.connections],
      }))
      addNotification({ role: 'company', title: 'Nouvelle candidature', body: `${athlete?.firstName ?? 'Un athlète'} a répondu à votre opportunité.`, type: 'application', href: '/app/entreprise/candidatures' })
      return conversationId
    },
    askAboutBrief(briefId, athleteId) {
      const brief = state.briefs.find((item) => item.id === briefId)
      if (!brief) throw new Error('Opportunité introuvable.')
      const existing = state.conversations.find((conversation) => conversation.athleteId === athleteId && conversation.companyId === brief.companyId && conversation.contextId === briefId)
      if (existing) return existing.id
      const conversationId = `conv-${crypto.randomUUID()}`
      const now = new Date().toISOString()
      const connection: ConnectionIntent = { id: `conn-${crypto.randomUUID()}`, athleteId, companyId: brief.companyId, briefId, conversationId, initiatedBy: 'athlete', status: 'conversation_started', createdAt: now, updatedAt: now }
      setState((current) => ({
        ...current,
        conversations: [{ id: conversationId, athleteId, companyId: brief.companyId, subject: `Question — ${brief.title}`, contextType: 'brief', contextId: briefId, unreadByAthlete: 0, unreadByCompany: 1, messages: [{ id: `m-${crypto.randomUUID()}`, senderRole: 'athlete', senderName: current.currentUser?.displayName ?? 'Athlète', body: `Bonjour, j’ai une question au sujet de l’opportunité « ${brief.title} » et de ses disponibilités.`, createdAt: now, read: false }] }, ...current.conversations],
        connections: [connection, ...current.connections],
      }))
      return conversationId
    },
    createProposal(input) {
      const created: Proposal = {
        id: `prop-${crypto.randomUUID()}`,
        athleteId: input.athleteId,
        companyId: input.companyId,
        briefId: input.briefId,
        title: input.title,
        type: input.type ?? 'Sponsoring financier',
        amount: input.amount,
        inKindValue: input.inKindValue ?? 0,
        description: input.description ?? 'Proposition créée depuis AthLink.',
        startDate: input.startDate ?? new Date().toISOString().slice(0, 10),
        endDate: input.endDate ?? new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
        imageRights: input.imageRights ?? 'Web et réseaux sociaux France pendant la durée du partenariat',
        exclusivity: input.exclusivity ?? 'Aucune',
        territory: input.territory ?? 'France',
        paymentTerms: input.paymentTerms ?? '50 % à la signature, 50 % à mi-parcours',
        status: 'sent',
        deliverables: input.deliverables ?? [{ id: `pd-${crypto.randomUUID()}`, title: 'Publication partenaire', quantity: 2, dueDate: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10), proofType: 'Lien + statistiques' }],
        versions: [{ id: `pv-${crypto.randomUUID()}`, number: 1, amount: input.amount, durationMonths: 6, notes: 'Proposition initiale', createdAt: new Date().toISOString(), createdBy: state.currentUser?.role === 'athlete' ? 'athlete' : 'company' }],
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 14 * 86400000).toISOString(),
      }
      setState((current) => ({ ...current, proposals: [created, ...current.proposals], connections: current.connections.some((item) => item.athleteId === created.athleteId && item.companyId === created.companyId) ? current.connections.map((item) => item.athleteId === created.athleteId && item.companyId === created.companyId ? { ...item, status: 'proposal_in_progress', updatedAt: new Date().toISOString() } : item) : [{ id: `conn-${crypto.randomUUID()}`, athleteId: created.athleteId, companyId: created.companyId, briefId: created.briefId, initiatedBy: current.currentUser?.role === 'athlete' ? 'athlete' : 'company', status: 'proposal_in_progress', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }, ...current.connections] }))
      addNotification({ role: state.currentUser?.role === 'company' ? 'athlete' : 'company', title: 'Nouvelle proposition', body: created.title, type: 'proposal', href: '/app/athlete/propositions' })
      return created
    },
    counterProposal(proposalId, amount, notes) {
      setState((current) => ({
        ...current,
        proposals: current.proposals.map((proposal) => proposal.id !== proposalId ? proposal : {
          ...proposal,
          amount,
          status: 'countered',
          versions: [...proposal.versions, { id: `pv-${crypto.randomUUID()}`, number: proposal.versions.length + 1, amount, durationMonths: Math.max(1, Math.round((new Date(proposal.endDate).getTime() - new Date(proposal.startDate).getTime()) / 2_592_000_000)), notes, createdAt: new Date().toISOString(), createdBy: current.currentUser?.role === 'athlete' ? 'athlete' : 'company' }],
        }),
      }))
      addNotification({ role: state.currentUser?.role === 'company' ? 'athlete' : 'company', title: 'Contre-proposition reçue', body: `Un nouveau montant de ${amount.toLocaleString('fr-FR')} € est proposé.`, type: 'proposal', href: '/app/entreprise/propositions' })
    },
    updateProposalStatus(proposalId, status) {
      setState((current) => ({ ...current, proposals: current.proposals.map((proposal) => proposal.id === proposalId ? { ...proposal, status } : proposal) }))
    },
    generateContract(proposalId) {
      const proposal = state.proposals.find((item) => item.id === proposalId)
      if (!proposal) throw new Error('Proposition introuvable.')
      const existing = state.contracts.find((contract) => contract.proposalId === proposalId)
      if (existing) return existing
      const created: Contract = {
        id: `ctr-${crypto.randomUUID()}`,
        proposalId,
        athleteId: proposal.athleteId,
        companyId: proposal.companyId,
        type: proposal.type,
        title: `Contrat — ${proposal.title}`,
        amount: proposal.amount,
        commissionRate: 0.135,
        status: 'ready',
        version: 1,
        clauses: ['Identité et objet', 'Montant et nature du soutien', 'Durée et calendrier', 'Contreparties et preuves', 'Droits à l’image', 'Paiement et commission AthLink', 'Résiliation', 'Responsabilité et litiges'],
        signatures: [{ role: 'athlete', signed: false }, { role: 'company', signed: false }],
        createdAt: new Date().toISOString(),
      }
      setState((current) => ({
        ...current,
        contracts: [created, ...current.contracts],
        proposals: current.proposals.map((item) => item.id === proposalId ? { ...item, status: 'contracted' } : item),
      }))
      addNotification({ role: 'all', title: 'Contrat prêt à relire', body: created.title, type: 'contract', href: '/app/entreprise/contrats' })
      return created
    },
    signContract(contractId, role) {
      setState((current) => {
        const contractsNext = current.contracts.map((contract) => {
          if (contract.id !== contractId) return contract
          const signatures = contract.signatures.map((signature) => signature.role === role ? { ...signature, signed: true, signedAt: new Date().toISOString(), signerName: current.currentUser?.displayName } : signature)
          const allSigned = signatures.every((signature) => signature.signed)
          const status: Contract['status'] = allSigned ? 'signed' : 'partially_signed'
          return { ...contract, signatures, status, effectiveDate: allSigned ? new Date().toISOString().slice(0, 10) : contract.effectiveDate }
        })
        return { ...current, contracts: contractsNext }
      })
      addNotification({ role: role === 'athlete' ? 'company' : 'athlete', title: 'Signature ajoutée', body: 'Le contrat a été signé par l’autre partie.', type: 'contract', href: '/app/athlete/contrats' })
    },
    paySandbox(contractId) {
      const contract = state.contracts.find((item) => item.id === contractId)
      if (!contract) throw new Error('Contrat introuvable.')
      if (contract.status !== 'signed' && contract.status !== 'active') throw new Error('Le contrat doit être signé par les deux parties avant paiement.')
      const proposal = state.proposals.find((item) => item.id === contract.proposalId)
      const breakdown = calculatePaymentBreakdown(contract.amount)
      const partnershipId = `part-${crypto.randomUUID()}`
      const generatedDeliverables = (proposal?.deliverables ?? []).map((item) => ({
        id: `del-${crypto.randomUUID()}`,
        partnershipId,
        title: item.title,
        description: `${item.quantity} unité${item.quantity > 1 ? 's' : ''} à réaliser.`,
        responsibleRole: 'athlete' as const,
        dueDate: item.dueDate,
        status: 'todo' as DeliverableStatus,
        proofType: item.proofType,
        comments: [],
        proofs: [],
      }))
      const partnership: Partnership = {
        id: partnershipId,
        contractId,
        athleteId: contract.athleteId,
        companyId: contract.companyId,
        title: contract.title.replace('Contrat — ', ''),
        status: 'active',
        startDate: proposal?.startDate ?? new Date().toISOString().slice(0, 10),
        endDate: proposal?.endDate ?? new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
        completionRate: 0,
        renewalProbability: 50,
        deliverableIds: generatedDeliverables.map((item) => item.id),
      }
      setState((current) => ({
        ...current,
        contracts: current.contracts.map((item) => item.id === contractId ? { ...item, status: 'active' } : item),
        partnerships: [partnership, ...current.partnerships],
        deliverables: [...generatedDeliverables, ...current.deliverables],
        connections: current.connections.map((item) => item.athleteId === contract.athleteId && item.companyId === contract.companyId ? { ...item, status: 'partnership_created', updatedAt: new Date().toISOString() } : item),
        payments: [{
          id: `pay-${crypto.randomUUID()}`,
          contractId,
          partnershipId,
          companyId: contract.companyId,
          athleteId: contract.athleteId,
          ...breakdown,
          status: 'paid',
          scheduleLabel: 'Paiement sandbox',
          dueDate: new Date().toISOString().slice(0, 10),
          paidAt: new Date().toISOString(),
          sandbox: true,
        }, ...current.payments],
      }))
      addNotification({ role: 'all', title: 'Partenariat activé', body: 'Le paiement sandbox est enregistré et les livrables sont disponibles.', type: 'payment', href: '/app/athlete/partenariats' })
    },
    submitProof(deliverableId, caption, url = 'https://example.com/preuve-demo') {
      setState((current) => ({
        ...current,
        deliverables: current.deliverables.map((deliverable) => deliverable.id !== deliverableId ? deliverable : {
          ...deliverable,
          status: 'submitted',
          proofs: [...deliverable.proofs, { id: `proof-${crypto.randomUUID()}`, type: deliverable.proofType, url, caption, submittedAt: new Date().toISOString() }],
        }),
      }))
      addNotification({ role: 'company', title: 'Preuve soumise', body: caption, type: 'proof', href: '/app/entreprise/partenariats' })
    },
    reviewProof(deliverableId, approved, comment) {
      setState((current) => ({
        ...current,
        deliverables: current.deliverables.map((deliverable) => {
          if (deliverable.id !== deliverableId) return deliverable
          const proofs = deliverable.proofs.map((proof, index) => index === deliverable.proofs.length - 1 ? { ...proof, reviewedAt: new Date().toISOString(), reviewerComment: comment } : proof)
          return { ...deliverable, status: approved ? 'validated' : 'changes_requested', comments: [...deliverable.comments, comment], proofs }
        }),
        partnerships: current.partnerships.map((partnership) => {
          const relevant = current.deliverables.filter((deliverable) => deliverable.partnershipId === partnership.id)
          if (!relevant.some((deliverable) => deliverable.id === deliverableId)) return partnership
          const validatedCount = relevant.filter((deliverable) => deliverable.status === 'validated' || (deliverable.id === deliverableId && approved)).length
          return { ...partnership, completionRate: Math.round((validatedCount / Math.max(1, relevant.length)) * 100), renewalProbability: Math.min(95, partnership.renewalProbability + (approved ? 5 : -8)) }
        }),
      }))
      addNotification({ role: 'athlete', title: approved ? 'Preuve validée' : 'Correction demandée', body: comment, type: 'proof', href: '/app/athlete/livrables' })
    },
    updateMatchingWeights(weights) {
      setState((current) => ({ ...current, matchingWeights: weights }))
    },
    markNotificationRead(id) {
      setState((current) => ({ ...current, notifications: current.notifications.map((notification) => notification.id === id ? { ...notification, read: true } : notification) }))
    },
    markAllNotificationsRead() {
      setState((current) => ({ ...current, notifications: current.notifications.map((notification) => ({ ...notification, read: true })) }))
    },
    updateAthleteVerification(athleteId, status) {
      setState((current) => ({ ...current, athletes: current.athletes.map((athlete) => athlete.id === athleteId ? { ...athlete, verified: status } : athlete) }))
    },
    resolveAdminReport(reportId) {
      setState((current) => ({ ...current, adminReports: current.adminReports.map((report) => report.id === reportId ? { ...report, status: 'resolved' } : report) }))
    },
    toggleSubscription(role) {
      setState((current) => ({ ...current, subscription: { ...current.subscription, [role]: current.subscription[role] === 'premium' ? 'free' : 'premium' } }))
    },
    updateAthleteProfile(athleteId, patch) {
      setState((current) => ({ ...current, athletes: current.athletes.map((athlete) => athlete.id === athleteId ? { ...athlete, ...patch } : athlete) }))
    },
    updateCompanyProfile(companyId, patch) {
      setState((current) => ({ ...current, companies: current.companies.map((company) => company.id === companyId ? { ...company, ...patch } : company) }))
    },
    updateAthletePack(athleteId, packId, patch) {
      setState((current) => ({ ...current, athletes: current.athletes.map((athlete) => athlete.id !== athleteId ? athlete : { ...athlete, packs: athlete.packs.map((pack) => pack.id === packId ? { ...pack, ...patch } : pack) }) }))
    },
    resetDemo() {
      localStorage.removeItem(STORAGE_KEY)
      setState(structuredClone(initialState))
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state])

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useAppStore() {
  const value = useContext(StoreContext)
  if (!value) throw new Error('useAppStore doit être utilisé dans AppStoreProvider.')
  return value
}
